import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { findSpellById } from '@/data/spells';
import { cn } from '@/lib/utils';
import { type ChoiceGroup, getMoveChoiceGroups, type Move, type MoveChoice } from '@/schemas/move';
import { isMoveChoiceAvailable, isMoveChoiceFree } from './moveChoiceConstraints';
import { SpellChoiceCard } from './SpellChoiceCard';
import { buildSpellLookup, isSpellChoiceAccessible } from './spellChoiceHelpers';

/**
 * **FI-76 (2026-05-22)** — stile card-like per le row di scelta (radio /
 * checkbox). Border sottile + bg paper-bright + hover ink-border + active
 * bg paper-soft. Rende evidente l'area cliccabile + dà feedback su hover
 * desktop e tap mobile. Click ovunque sulla `<label htmlFor>` triggera
 * l'input collegato (browser native, stesso pattern di MoveCard).
 *
 * Padding compatto rispetto a ChoiceRow (`px-[0.65em] py-[0.4em]` vs
 * `px-[0.7em] py-[0.5em]`): le liste di scelta possono avere 7+ opzioni
 * (es. Miglioramenti Arma Prescelta) e la densità verticale serve.
 */
const CHOICE_OPTION_ROW_BASE_CLASS =
  'border-rule bg-paper-bright flex cursor-pointer items-start gap-2 border px-[0.65em] py-[0.4em] transition-[background-color,border-color] duration-150 hover:border-ink active:bg-paper-soft';
const CHOICE_OPTION_ROW_SELECTED_CLASS = 'border-ink bg-paper-soft';
const CHOICE_OPTION_ROW_DISABLED_CLASS = 'pointer-events-none cursor-not-allowed opacity-45';

/**
 * Editor delle `choiceGroups` (FI-18 Fase 2 + FI-32) di una mossa, unificato
 * per shorthand `choices` (1 gruppo virtuale `'main'`) e multi-gruppo.
 *
 * **FI-18 — render per gruppo**:
 *  - `RadioGroup` shadcn se `pickCount === 1`
 *  - Checkbox-cap (disabilita opzioni non scelte quando raggiunto
 *    `pickCount`) se `pickCount > 1`
 *  - Mostra `label` + `description` opzionale + counter "X di Y"
 *
 * **FI-32 — vincoli archetipo sulle opzioni**:
 *  - `requiresArchetype`: opzioni non disponibili vengono **nascoste** dalla
 *    UI (filtrate via {@link isMoveChoiceAvailable}). Esempio: Tradizione
 *    Sacra *Natura* di `paladino.ordine-sacro` → solo Elfo.
 *  - `freeForArchetype`: opzioni "gratis" per l'archetipo corrente sono
 *    rese con badge "(gratis)", **non rimovibili** (checkbox disabled +
 *    checked, oppure non-cliccabili tra i radio) e **non contate** nel cap
 *    `pickCount`. L'auto-add nella selezione avviene in `useMoveSetupState`.
 *    Esempi: «truce» Mezz'orco-Warlock, «immunità ai veleni» Nano-Warlock.
 *
 * Estratto come sotto-componente di `MoveSetupEditor` per rispettare i
 * limiti `max-lines: 400` / `max-lines-per-function: 100`.
 */
interface MoveChoiceGroupsFieldProps {
  move: Move;
  /** Selezioni per gruppo. Il gruppo virtuale `'main'` (shorthand) usa la
   * stessa mappa: il padre lo proietta da/verso `selectedChoices`. */
  selectedByGroup: Record<string, string[]>;
  archetypeId: string | undefined;
  onChange: (groupId: string, selectedIds: string[]) => void;
  /**
   * **D8a** — override del `pickCount` statico di uno o più gruppi, calcolato
   * runtime dal consumer (es. da `MoveRef.recruitOutcome` per le mosse-source
   * di Follower con `recruitRoll`). Se un gruppo `id` è presente in questa
   * mappa, il suo valore sovrascrive `group.pickCount`. Opzionale: assente per
   * mosse senza recruitRoll.
   */
  pickCountOverrideByGroup?: Record<string, number> | undefined;
  /**
   * **FI-79 (2026-05-23)** — IDs delle mosse possedute dal PG. Usato per
   * disabilitare le spell-options che hanno `Spell.availableTo[].requiresMove`
   * non soddisfatto (es. `chierico.curare-ferite-moderate` richiede
   * `chierico.primo-soccorso-migliorato`). Opzionale: se non passato, il
   * filtro accessibility spell non viene applicato (graceful fallback).
   */
  ownedMoveIds?: readonly string[] | undefined;
}

export function MoveChoiceGroupsField({
  move,
  selectedByGroup,
  archetypeId,
  onChange,
  pickCountOverrideByGroup,
  ownedMoveIds,
}: MoveChoiceGroupsFieldProps) {
  const groups = getMoveChoiceGroups(move);
  if (groups.length === 0) return null;

  return (
    <div className="grid gap-3">
      {groups.map((group) => {
        const override = pickCountOverrideByGroup?.[group.id];
        // D8a: applica override del pickCount creando una copia del gruppo.
        // I `group.options` restano gli stessi; cambia solo il cap delle
        // selezioni "paid" e l'header counter ("X di Y").
        const effectiveGroup = override !== undefined ? { ...group, pickCount: override } : group;
        return (
          <ChoiceGroupControl
            key={group.id}
            group={effectiveGroup}
            moveId={move.id}
            selected={selectedByGroup[group.id] ?? []}
            archetypeId={archetypeId}
            ownedMoveIds={ownedMoveIds}
            onChange={(next) => onChange(group.id, next)}
          />
        );
      })}
    </div>
  );
}

interface ChoiceGroupControlProps {
  group: ChoiceGroup;
  moveId: string;
  selected: string[];
  archetypeId: string | undefined;
  ownedMoveIds?: readonly string[] | undefined;
  onChange: (next: string[]) => void;
}

function ChoiceGroupControl({
  group,
  moveId,
  selected,
  archetypeId,
  ownedMoveIds,
  onChange,
}: ChoiceGroupControlProps) {
  const visibleOptions = group.options.filter((opt) => isMoveChoiceAvailable(opt, archetypeId));
  // FI-32: conteggio "paid" esclude le opzioni free per l'archetipo
  // corrente (sono bonus, non scelte).
  const paidSelectedCount = selected.filter((id) => {
    const opt = group.options.find((o) => o.id === id);
    return opt ? !isMoveChoiceFree(opt, archetypeId) : true;
  }).length;

  return (
    <div className="grid gap-2">
      <GroupHeader group={group} paidSelectedCount={paidSelectedCount} />
      {group.pickCount === 1 ? (
        <RadioGroupControl
          group={group}
          moveId={moveId}
          options={visibleOptions}
          selected={selected[0] ?? ''}
          archetypeId={archetypeId}
          ownedMoveIds={ownedMoveIds}
          onChange={(v) => onChange([v])}
        />
      ) : (
        <CheckboxCapControl
          group={group}
          moveId={moveId}
          options={visibleOptions}
          selected={selected}
          archetypeId={archetypeId}
          ownedMoveIds={ownedMoveIds}
          paidSelectedCount={paidSelectedCount}
          onChange={onChange}
        />
      )}
    </div>
  );
}

function GroupHeader({
  group,
  paidSelectedCount,
}: {
  group: ChoiceGroup;
  paidSelectedCount: number;
}) {
  // Etichetta vuota su gruppo virtuale shorthand `'main'` con `pickCount: 1`:
  // l'header verrebbe duplicato col nome della mossa nel parent. La saltiamo.
  // Con `pickCount > 1` invece mostriamo comunque il counter (es. Famiglio:
  // "0 di 2 scelte → 2 di 2") per dare feedback all'utente sullo stato di
  // completamento del setup.
  if (group.id === 'main' && group.label.length === 0 && group.pickCount === 1) return null;
  const displayLabel = group.label.length > 0 ? group.label : 'Scelte';
  return (
    <div className="grid gap-0.5">
      <p className="text-sm font-medium">
        {displayLabel}
        {group.pickCount > 1 && (
          <span className="ml-1 text-xs text-muted-foreground">
            ({paidSelectedCount} di {group.pickCount})
          </span>
        )}
      </p>
      {group.description && <p className="text-xs text-muted-foreground">{group.description}</p>}
    </div>
  );
}

type Option = ChoiceGroup['options'][number];

// `buildSpellLookup` + `isSpellChoiceAccessible` estratti in
// `./SpellChoiceCard.tsx` (FI-79) per rispettare `max-lines: 400`.

function RadioGroupControl({
  group,
  moveId,
  options,
  selected,
  archetypeId,
  ownedMoveIds,
  onChange,
}: {
  group: ChoiceGroup;
  moveId: string;
  options: readonly Option[];
  selected: string;
  archetypeId: string | undefined;
  ownedMoveIds: readonly string[] | undefined;
  onChange: (value: string) => void;
}) {
  const spellLookup = buildSpellLookup(options);
  return (
    <RadioGroup value={selected} onValueChange={onChange} className="gap-2">
      {options.map((opt) => {
        const inputId = `cg-${moveId}-${group.id}-${opt.id}`;
        // FI-32: opzione free è non-rimovibile (disabled) ma resta visibile
        // come checked. Per `pickCount=1` (radio) le opzioni free sono un
        // edge case raro; la disabilitamo per coerenza.
        const isFree = isMoveChoiceFree(opt, archetypeId);
        const isSelected = selected === opt.id;
        const spell = spellLookup?.get(opt.id);
        if (spell) {
          // FI-79: spell con `requiresMove` non soddisfatto → disabled
          // (impedisce selezione, preserva info via option.description tag).
          const isAccessible =
            ownedMoveIds === undefined
              ? true
              : isSpellChoiceAccessible(spell, archetypeId, ownedMoveIds);
          const disabled = isFree || !isAccessible;
          return (
            <SpellChoiceCard
              key={opt.id}
              option={opt}
              spell={spell}
              inputId={inputId}
              checked={isSelected}
              disabled={disabled}
              control={
                <RadioGroupItem value={opt.id} id={inputId} disabled={disabled} className="mt-0" />
              }
            />
          );
        }
        return (
          <label
            key={opt.id}
            htmlFor={inputId}
            className={cn(
              CHOICE_OPTION_ROW_BASE_CLASS,
              isSelected && CHOICE_OPTION_ROW_SELECTED_CLASS,
              isFree && CHOICE_OPTION_ROW_DISABLED_CLASS,
            )}
          >
            <RadioGroupItem value={opt.id} id={inputId} disabled={isFree} className="mt-1" />
            <OptionLabel option={opt} isFree={isFree} />
          </label>
        );
      })}
    </RadioGroup>
  );
}

function CheckboxCapControl({
  group,
  moveId,
  options,
  selected,
  archetypeId,
  ownedMoveIds,
  paidSelectedCount,
  onChange,
}: {
  group: ChoiceGroup;
  moveId: string;
  options: readonly Option[];
  selected: string[];
  archetypeId: string | undefined;
  ownedMoveIds: readonly string[] | undefined;
  paidSelectedCount: number;
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="grid gap-1">
      {options.map((opt) => (
        <OptionRow
          key={opt.id}
          group={group}
          moveId={moveId}
          option={opt}
          selected={selected}
          archetypeId={archetypeId}
          ownedMoveIds={ownedMoveIds}
          paidSelectedCount={paidSelectedCount}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

interface OptionRowProps {
  group: ChoiceGroup;
  moveId: string;
  option: Option;
  selected: string[];
  archetypeId: string | undefined;
  ownedMoveIds: readonly string[] | undefined;
  paidSelectedCount: number;
  onChange: (next: string[]) => void;
}

function OptionRow({
  group,
  moveId,
  option,
  selected,
  archetypeId,
  ownedMoveIds,
  paidSelectedCount,
  onChange,
}: OptionRowProps) {
  const isFree = isMoveChoiceFree(option, archetypeId);
  const inputId = `cg-${moveId}-${group.id}-${option.id}`;
  // FI-8b: opzioni `allowDuplicate: true` usano stepper "+/-" invece di checkbox
  // binaria; il count cresce fino a paidSelectedCount === pickCount.
  if (option.allowDuplicate) {
    return (
      <DuplicableOptionRow
        inputId={inputId}
        option={option}
        selected={selected}
        paidSelectedCount={paidSelectedCount}
        pickCount={group.pickCount}
        onChange={onChange}
      />
    );
  }
  const checked = selected.includes(option.id);
  // FI-32: opzioni free sono sempre checked + non rimovibili; il cap si applica
  // solo alle "paid". Non-free options sono disable quando paid === pickCount.
  const disabled = isFree || (!checked && paidSelectedCount >= group.pickCount);
  // FI-79: se l'option è una spell del catalogo, render via <SpellCard>
  // (SSOT riusato) invece del classico label+checkbox. Pattern coerente
  // con SpellPickPanel.PickSpellRow.
  const spell = findSpellById(option.id);
  if (spell) {
    // FI-79: spell con `requiresMove` non soddisfatto → disabled (cumula
    // col cap disabling già calcolato sopra).
    const isAccessible =
      ownedMoveIds === undefined ? true : isSpellChoiceAccessible(spell, archetypeId, ownedMoveIds);
    const spellDisabled = disabled || !isAccessible;
    return (
      <SpellChoiceCard
        option={option}
        spell={spell}
        inputId={inputId}
        checked={checked}
        disabled={spellDisabled}
        control={
          <Checkbox
            id={inputId}
            checked={checked}
            disabled={spellDisabled}
            onCheckedChange={(state) => {
              if (state === true) onChange([...selected, option.id]);
              else onChange(selected.filter((x) => x !== option.id));
            }}
            className="mt-0"
          />
        }
      />
    );
  }
  return (
    <label
      htmlFor={inputId}
      className={cn(
        CHOICE_OPTION_ROW_BASE_CLASS,
        checked && CHOICE_OPTION_ROW_SELECTED_CLASS,
        disabled && CHOICE_OPTION_ROW_DISABLED_CLASS,
      )}
    >
      <Checkbox
        id={inputId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(state) => {
          if (state === true) onChange([...selected, option.id]);
          else onChange(selected.filter((x) => x !== option.id));
        }}
        className="mt-1"
      />
      <OptionLabel option={option} isFree={isFree} />
    </label>
  );
}

interface DuplicableOptionRowProps {
  inputId: string;
  option: Option;
  selected: string[];
  paidSelectedCount: number;
  pickCount: number;
  onChange: (next: string[]) => void;
}

function DuplicableOptionRow({
  inputId,
  option,
  selected,
  paidSelectedCount,
  pickCount,
  onChange,
}: DuplicableOptionRowProps) {
  const count = selected.filter((id) => id === option.id).length;
  const canIncrement = paidSelectedCount < pickCount;
  const canDecrement = count > 0;
  const decrement = (): void => {
    // Rimuove una sola occorrenza, conservando le altre.
    const idx = selected.lastIndexOf(option.id);
    if (idx === -1) return;
    onChange([...selected.slice(0, idx), ...selected.slice(idx + 1)]);
  };
  const increment = (): void => {
    onChange([...selected, option.id]);
  };
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 inline-flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-6 w-6"
          aria-label={`Riduci ${option.label}`}
          disabled={!canDecrement}
          onClick={decrement}
        >
          –
        </Button>
        <span
          id={inputId}
          aria-live="polite"
          className="min-w-[1.25rem] text-center text-sm font-medium tabular-nums"
        >
          {count}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-6 w-6"
          aria-label={`Aumenta ${option.label}`}
          disabled={!canIncrement}
          onClick={increment}
        >
          +
        </Button>
      </div>
      <OptionLabel option={option} isFree={false} />
    </div>
  );
}

/**
 * **FI-76**: rimosso `<Label htmlFor>` interno — l'`htmlFor` è ora sulla
 * `<label>` esterna che wrappa l'intera row (`<label>` di MoveCard-style
 * binding click-area). OptionLabel diventa puramente visuale.
 */
function OptionLabel({ option, isFree }: { option: MoveChoice; isFree: boolean }) {
  return (
    <span className="text-sm leading-snug">
      {option.label}
      {isFree && <span className="ml-1 text-xs font-medium text-primary">(gratis)</span>}
      {option.description && (
        <span className="ml-1 text-xs text-muted-foreground">({option.description})</span>
      )}
    </span>
  );
}

// `SpellChoiceCard` (FI-79) estratto in `./SpellChoiceCard.tsx` per
// rispettare `max-lines: 400` su questo file.
